import {
  Datasheet,
  useDeviceDatasheetBuilder,
} from 'features/Integrations/features/ConnectedDevices/features/Datasheet';
import {
  selectSelectedDeviceId,
  selectUSGSOpen,
} from 'features/Integrations/state/selectors/deviceSelectors';
import {
  clearDeviceSettings,
  openStageSettings,
  openUSGSSettings,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
  useGetDeviceTypeMapQuery,
} from 'services/devices/devices.api';
import ConnectedDevicesLoadingState from '../../../../components/ConnectedDevicesLoadingState/ConnectedDevicesLoadingState';
import { isBuoyDevice } from '../../../Buoy/utils/buoyDevice.utils';
import {
  ClearDatasheetParams,
  getPage0Did,
  resolveDeviceFromSearch,
  setDataSheetURLParams,
} from '../../utils/url.utils';
import DeviceGroupList from '../DeviceGroupList/DeviceGroupList';
import DeviceListAccordion from '../DeviceListAccordion/DeviceListAccordion';
import DeviceTypeList from '../DeviceTypeList/DeviceTypeList';

const List = ({
  showGroups,
  showCurrentDevice,
  setShowCurrentDevice = () => {},
  widget = false,
  startTime, // the page's query window; the open datasheet fetches its series in it
  endTime,
}) => {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isSuccess: devicesLoaded,
  } = useGetConnectedDevicesQuery();
  const { data: deviceTypes = {}, isLoading: deviceTypesLoading } = useGetDeviceTypeMapQuery();
  const usgsOpen = useSelector(selectUSGSOpen);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const [activeDatasheet, setActiveDatasheet] = useState(null);
  const [groupsVisibleTypes] = useState({
    Flood: true,
    'Manual Road Closure': true,
    'Technology Bridge_Flashing Lights': true,
    'Technology Bridge_Siren/Giant Voice': true,
    'Overwatch VMS': true,
  });

  const currentDevice = useMemo(() => {
    if (selectedDeviceId == null) return null;
    return connectedDevices.find((d) => d.id === selectedDeviceId) || null;
  }, [connectedDevices, selectedDeviceId]);

  // Datasheet Helper
  // Sets local state and (in fullscreen mode) syncs ?uid= + ?did= into the URL.
  const openDatasheet = useCallback(
    (device) => {
      if (!device) return;
      const isStage = DeviceIsSystemType(device, 'Flood');
      const isUSGS = device.system_type === 'USGS';
      const isBuoy = isBuoyDevice(device);
      if (!isStage && !isUSGS && !isBuoy) return;
      setShowCurrentDevice(false);
      dispatch(setSelectedDeviceId(device.id));
      setActiveDatasheet(isUSGS ? 'usgs' : isBuoy ? 'buoy' : 'Flood');
      if (!widget) {
        setParams(
          (_prev) => {
            // Read the live URL — window.location.search is updated synchronously by
            // navigate()'s history.replaceState, so it always reflects graph-click
            // navigation regardless of React's render-batching state.
            const liveParams = new URLSearchParams(window.location.search);
            const liveUid = liveParams.get('uid');
            const liveDid = liveParams.get('did');
            // Preserve a specific did already set (e.g., from a graph data-point click)
            // rather than resetting to page 0.
            if (liveUid === String(device.id) && liveDid) return liveParams;
            return setDataSheetURLParams(_prev, device.id, getPage0Did(device));
          },
          { replace: true },
        );
      }
    },
    [setParams, widget],
  );

  // Open the AWARE datasheet when external components ask to show the current device
  useEffect(() => {
    if (!showCurrentDevice || !currentDevice) return;
    openDatasheet(currentDevice);
  }, [showCurrentDevice, currentDevice]);

  // Also open datasheet when Redux requests USGS settings
  useEffect(() => {
    if (usgsOpen && selectedDeviceId != null && connectedDevices) {
      const device = connectedDevices.find((d) => d.id === selectedDeviceId) || null;
      openDatasheet(device);
    }
  }, [usgsOpen, selectedDeviceId, connectedDevices]);

  const onOpenDeviceData = useCallback(
    (deviceId) => {
      if (!connectedDevices) return;
      let device = null;
      if (typeof deviceId !== 'undefined' && deviceId !== null) {
        device = connectedDevices.find((d) => String(d.id) === String(deviceId)) || null;
      } else if (selectedDeviceId != null) {
        device = connectedDevices.find((d) => d.id === selectedDeviceId) || null;
      }
      openDatasheet(device);
    },
    [connectedDevices, selectedDeviceId, openDatasheet],
  );

  // Clear Bad Params
  const clearUidParams = useCallback(
    () => !widget && setParams(ClearDatasheetParams, { replace: true }),
    [setParams, widget],
  );

  // Clear ?uid= and ?did= on unmount (fullscreen only — widgets don't own URL params).
  useEffect(() => {
    if (widget) return;
    return () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('uid');
      url.searchParams.delete('did');
      window.history.replaceState(null, '', url);
    };
  }, []);

  const openedFromUrlRef = useRef(false);
  useEffect(() => {
    if (widget) return; // widgets don't restore state from URL params
    if (openedFromUrlRef.current) return;
    if (!devicesLoaded) return;
    openedFromUrlRef.current = true;
    const result = resolveDeviceFromSearch(window.location.search, connectedDevices);
    if (result.status === 'invalid') {
      clearUidParams();
      return;
    }
    if (result.status === 'missing') return;
    // status === 'ok' — URL already has the params, just restore local state.
    dispatch(setSelectedDeviceId(result.device.id));
    setActiveDatasheet(result.datasheetType);
    dispatch(setSelectedDeviceId(result.device.id));
  }, [devicesLoaded, connectedDevices, dispatch, clearUidParams]);

  // Close datasheet handler
  const onDataSheetClose = () => {
    dispatch(setSelectedDeviceId(null));
    setActiveDatasheet(null);
    try {
      dispatch(clearDeviceSettings());
    } catch (e) {}
    if (!widget) setParams(ClearDatasheetParams, { replace: true });
  };

  const onStageSettingsOpen = (e, device) => {
    if (DeviceIsSystemType(device, 'Flood')) {
      e.stopPropagation();
      dispatch(openStageSettings(device.id));
    }
  };

  const onUSGSDataSheetOpen = (e, device) => {
    if (device?.system_type === 'USGS') {
      e.stopPropagation();
      dispatch(openUSGSSettings(device.id));
    }
  };

  // map selected device id used for datasheets to an actual device object
  const selectedDeviceById = useMemo(
    () => connectedDevices.find((d) => d.id === selectedDeviceId) || null,
    [connectedDevices, selectedDeviceId],
  );

  const handleDatasheetSettingsOpen = useCallback(
    (event) => {
      if (!selectedDeviceById) return;
      if (activeDatasheet === 'usgs') {
        onUSGSDataSheetOpen(event, selectedDeviceById);
        return;
      }
      if (activeDatasheet === 'Flood') {
        onStageSettingsOpen(event, selectedDeviceById);
      }
    },
    [activeDatasheet, selectedDeviceById],
  );

  // The sheet is open only for the three types it can draw. The series fetch is
  // gated on THAT, not on a device being selected: the graph's device dropdown
  // and the sessionStorage restore both move `selectedDeviceId` with no sheet on
  // screen, and a page fetched then is a request nobody reads.
  const datasheetOpen =
    !!selectedDeviceById &&
    (activeDatasheet === 'Flood' || activeDatasheet === 'usgs' || activeDatasheet === 'buoy');

  const datasheetProps = useDeviceDatasheetBuilder({
    type: activeDatasheet,
    device: selectedDeviceById,
    onClose: onDataSheetClose,
    onSettingsOpen: activeDatasheet === 'buoy' ? undefined : handleDatasheetSettingsOpen,
    startTime,
    endTime,
    fetchSeries: datasheetOpen,
  });

  // The datasheet is drawn IN FRONT of the list, and the list stays mounted
  // behind it (hidden, not unmounted). Before this the list was replaced, so
  // every open type dropdown and device row lost its state and Back came
  // back to a collapsed list. Mike, 2026-08-28: "it should allow us to go
  // back and not remove the selected devices we are graphing and close the
  // drop down we just went back from" — Back now closes only the datasheet.
  return (
    <>
      {datasheetOpen && <Datasheet key={selectedDeviceId} {...datasheetProps} />}
      <div
        className={`position-relative d-flex flex-column flex-fill h-100 min-h-0${datasheetOpen ? ' d-none' : ''}`}
      >
        <div className="mb-2 px-2 py-2 bg-primary-shadow border border-theme-strong rounded-2">
          <div className="fw-semibold h5 mb-0 text-center">
            {showGroups ? 'Groups' : 'All Devices'}
          </div>
        </div>

        <div className="flex-fill overflow-x-hidden overflow-y-auto z-3">
          <div className="d-none d-xl-block">
            {(connectedDevicesLoading || deviceTypesLoading) && (
              <div className="d-flex align-items-center justify-content-center py-2 small text-theme-muted">
                <ConnectedDevicesLoadingState message="Loading device list..." />
              </div>
            )}

            {!showGroups &&
              deviceTypes &&
              Array.isArray(deviceTypes) &&
              [...deviceTypes]
                .sort((a, b) => {
                  // Sort by system_type_id, then device_type_id
                  if (a.system_type_id !== b.system_type_id) {
                    return a.system_type_id - b.system_type_id;
                  }
                  return a.device_type_id - b.device_type_id;
                })
                .map((dType) => (
                  <DeviceTypeList
                    key={dType.device_type}
                    system={dType}
                    systemName={dType.system_type}
                    onOpenDevice={onOpenDeviceData}
                  />
                ))}

            {showGroups && (
              <DeviceGroupList
                onOpenDevice={onOpenDeviceData}
                visibleDeviceTypes={groupsVisibleTypes}
                widget={widget}
              />
            )}
          </div>

          {(connectedDevicesLoading || deviceTypesLoading) && (
            <div className="d-flex d-xl-none align-items-center justify-content-center py-2 small text-theme-muted">
              <ConnectedDevicesLoadingState message="Loading device list..." />
            </div>
          )}
          <DeviceListAccordion
            showGroups={showGroups}
            onOpenDevice={onOpenDeviceData}
            visibleDeviceTypes={groupsVisibleTypes}
            widget={widget}
          />
        </div>
      </div>
    </>
  );
};

export default List;